import { useState, useEffect, useRef } from 'react';

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function LaMaison() {
  const [heroRef, heroVis] = useReveal(0.1);
  const [txtRef, txtVis] = useReveal(0.15);
  const [sylRef, sylVis] = useReveal(0.15);
  const [sylTxtRef, sylTxtVis] = useReveal(0.2);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section className="relative h-[70vh] md:h-[80vh] flex items-center justify-center overflow-hidden bg-black">
        <img src="/photos/Sylvie-SMP_9.jpg" alt="Atelier Pernety"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.85) contrast(1) saturate(0.95)', objectPosition: '50% 45%' }} />
        <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.15)' }} />

        <div ref={heroRef} className={`relative z-10 text-center px-6 transition-all duration-[1.5s] ${heroVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-6 mb-10">
            <div className="w-16 h-px bg-white/30" />
            <img src="/photos/Logo_fd_transparent.png" alt="" className="h-12 w-auto"
              style={{ filter: 'invert(1) brightness(2)', opacity: 0.5 }} />
            <div className="w-16 h-px bg-white/30" />
          </div>
          <h1 className="text-white font-light" style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(40px, 5vw, 68px)'
          }}>
            La Maison
          </h1>
        </div>
      </section>

      {/* ── IDENTITE DE MARQUE ── */}
      <section className="py-24 md:py-32 px-6 bg-white">
        <div ref={txtRef} className={`max-w-2xl mx-auto transition-all duration-[1.5s] ${txtVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="space-y-8 text-gray-600 text-base md:text-lg leading-[1.9]">
            <p>
              Pernety est une maison de joaillerie qui imagine des bijoux pensés comme des <em>fragments de mémoire</em>.<br />
              <strong className="text-gray-900 font-semibold">Chaque création naît d'un souvenir</strong>, d'une émotion, d'une lumière particulière, d'un instant vécu quelque part dans le monde.
            </p>
            <p>
              Héritage de la terre, nos pierres sont choisies avec soin pour leur caractère unique, leur éclat singulier. Chaque bijou devient ainsi une présence intime à porter et à transmettre.
            </p>
          </div>

          <div className="mt-14 flex items-center justify-center gap-6 text-sm tracking-[0.3em] text-gray-400 font-light">
            <span>Paris</span>
            <span className="w-1 h-1 rounded-full bg-gray-400" />
            <span>Bangkok</span>
            <span className="w-1 h-1 rounded-full bg-gray-400" />
            <span>Le Monde</span>
          </div>
        </div>
      </section>

      {/* ── LA CREATRICE — Intro ── */}
      <section className="py-0 bg-black">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 min-h-[50vh] md:min-h-[60vh]">
          <div className="flex flex-col items-center justify-center text-center px-6 py-20 md:py-0">
            <h2 className="text-white font-light mb-6" style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(40px, 5vw, 68px)'
            }}>
              La créatrice
            </h2>
            <p className="text-white/50" style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(14px, 1.1vw, 17px)'
            }}>
              Un regard guidé par la pierre...
            </p>
          </div>
          <div className="relative overflow-hidden min-h-[400px] md:min-h-0 flex items-center justify-center bg-black p-10">
            <img src="/photos/IMG_9269.jpeg" alt="Dessin d'un bijou"
              className="max-w-[80%] max-h-[80%] object-contain shadow-2xl" />
          </div>
        </div>
      </section>

      {/* ── LA CREATRICE ── */}
      <section className="relative py-20 md:py-28 bg-white overflow-hidden">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 items-center gap-10 md:gap-0">
          <div ref={sylRef}
            className={`flex justify-center transition-all duration-[1.5s] ${sylVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <img src="/photos/IMG_0964.jpeg" alt="Sylvie"
              className="w-[300px] md:w-[380px] lg:w-[420px] h-auto object-cover shadow-lg" />
          </div>

          <div ref={sylTxtRef}
            className={`flex items-center px-8 md:px-10 lg:px-16 md:self-start md:pt-24 lg:pt-36 transition-all duration-[1.5s] delay-300 ${sylTxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="max-w-xl">
              <div className="space-y-6">
                <p className="text-gray-600 leading-[1.9] text-[15px]">
                  Gemmologue, j'ai été formée au Laboratoire Français de Gemmologie à Paris
                  puis à l'Asian Institute of Gemological Studies à Bangkok, où je me suis spécialisée dans les pierres de couleur.
                </p>
                <p className="text-gray-600 leading-[1.9] text-[15px]">
                  Curieuse et passionnée, je continue d'enrichir mes connaissances et d'affiner
                  mon regard sur les gemmes. Mes créations naissent toujours d'une pierre qui m'inspire
                  particulièrement, autour de laquelle le bijou prend forme.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
